'use client';

import { ChevronDown, ChevronRight, Folder, FolderOpen } from 'lucide-react';
import { useState } from 'react';

import type { BookmarkNode } from '@/features/portfolio/types/bookmarks';
import { cn } from '@/lib/utils';

import { BookmarkItem } from './bookmark-item';

interface FolderItemProps {
  node: BookmarkNode;
  level?: number;
  defaultOpen?: boolean;
}

function getNodeKey(node: BookmarkNode, level: number, index: number): string {
  if (node.type === 'link') {
    return `link-${level}-${node.url}-${index}`;
  }
  return `folder-${level}-${node.name}-${index}`;
}

export function FolderItem({
  node,
  level = 0,
  defaultOpen = false,
}: FolderItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (node.type === 'link') {
    return <BookmarkItem bookmark={node} level={level} />;
  }

  const hasChildren = node.children && node.children.length > 0;

  const handleToggle = () => {
    if (hasChildren) {
      setIsOpen(!isOpen);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (hasChildren && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  return (
    <div>
      <button
        type="button"
        className={cn(
          'flex items-center gap-2 py-2 hover:bg-accent2 cursor-pointer transition-colors border-b border-edge w-full text-left',
          level > 0 && 'border-l border-dashed border-edge',
          !hasChildren && 'cursor-default'
        )}
        style={{ paddingLeft: `${level * 16 + 16}px` }}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={!hasChildren}
        aria-expanded={hasChildren ? isOpen : undefined}
      >
        {hasChildren && (
          <div className="w-4 h-4 flex items-center justify-center shrink-0">
            {isOpen ? (
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        )}
        {!hasChildren && <div className="w-4 shrink-0" />}

        <div
          className={cn(
            'flex size-6 shrink-0 items-center justify-center rounded-lg select-none',
            'border border-muted-foreground/15 ring-1 ring-edge ring-offset-1 ring-offset-background',
            'bg-muted text-muted-foreground [&_svg]:size-4'
          )}
          aria-hidden
        >
          {isOpen ? (
            <FolderOpen className="w-4 h-4 text-muted-foreground" />
          ) : (
            <Folder className="w-4 h-4 text-muted-foreground" />
          )}
        </div>

        <span className="text-sm font-medium text-foreground">{node.name}</span>
      </button>

      {isOpen && hasChildren && (
        <div className="transition-all">
          {node.children.map((child, idx) => (
            <FolderItem
              key={getNodeKey(child, level + 1, idx)}
              node={child}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}
